<template>
  <div class="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-5xl mx-auto">
      <!-- Header -->
      <div class="text-center mb-12">
        <h1 class="text-5xl font-display text-gray-900 mb-4">
          Welkom bij Studio BoenkOp
        </h1>
        <template v-if="variant === 'studiocamera'">
          <p class="text-xl text-gray-600 mb-2" data-testid="hero-sub">
            Boek je fotoshoot in de studio, zonder fotograaf-gedoe
          </p>
          <p class="text-lg text-gray-500">
            De studiocamera zit erbij: één knop, en je foto's staan meteen op je gsm.
          </p>
        </template>
        <template v-else>
          <p class="text-xl text-gray-600 mb-2" data-testid="hero-sub">
            Reserveer je studiosessie eenvoudig online
          </p>
          <p class="text-lg text-gray-500">
            Professionele studio voor fotografie en videografie
          </p>
        </template>
        <p
          v-if="leadCode"
          class="mt-6 inline-block rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-800"
          data-testid="lead-code-note"
        >
          Eerste boeking bij ons? Je 20% korting staat klaar: code
          <strong class="font-mono">{{ leadCode }}</strong>. We vullen hem voor je in bij het betalen.
        </p>
      </div>

      <!-- Loyalty Tier Info (for authenticated users) -->
      <div v-if="authStore.isAuthenticated && myTierInfo" class="mb-8">
        <div class="bg-white shadow-lg rounded-lg p-6">
          <TierProgress
            :current-tier="myTierInfo.current_tier"
            :progress="myTierInfo.progress"
            :next-tier="nextTier"
            :total-spent-formatted="myTierInfo.total_spent_formatted"
            :show-benefits="true"
          />
          <div class="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-3">
            <router-link
              to="/credit-packages"
              class="inline-flex items-center px-4 py-2 bg-linear-to-r from-amber-500 to-amber-600 text-white rounded-md hover:from-amber-600 hover:to-amber-700 transition-all font-medium text-sm"
            >
              💰 Koop Krediet Pakketten
            </router-link>
            <router-link
              to="/profiel"
              class="inline-flex items-center px-4 py-2 bg-white border-2 border-amber-500 text-amber-600 rounded-md hover:bg-amber-50 transition-all font-medium text-sm"
            >
              👤 Bekijk je Profiel
            </router-link>
          </div>
        </div>
      </div>

      <!-- Booking Form -->
      <BookingForm />
      
      <!-- Features Section -->
      <div class="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div class="text-center">
          <div class="w-16 h-16 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <svg class="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-2">Professionele Studio</h3>
          <p class="text-gray-600">Volledig uitgeruste studio met professionele verlichting voor foto, video en audio</p>
        </div>
        
        <div class="text-center">
          <div class="w-16 h-16 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <svg class="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-2">Flexibele Tijden</h3>
          <p class="text-gray-600">Kies uit verschillende sessieduren en reserveer op het moment dat jou uitkomt</p>
        </div>
        
        <div class="text-center">
          <div class="w-16 h-16 mx-auto bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <svg class="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 mb-2">Eenvoudig Annuleren</h3>
          <p class="text-gray-600">Gratis annuleren tot 7 dagen voor je sessie, daarna 50% restitutie</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import BookingForm from '@/components/BookingForm.vue'
import TierProgress from '@/components/TierProgress.vue'
import { useAuthStore } from '@/stores/auth'
import { useLoyalty } from '@/composables/useLoyalty'
import { getLeadCode, heroVariant } from '@/utils/leadOffer'

const authStore = useAuthStore()
const { myTierInfo, nextTier, fetchMyTier } = useLoyalty()

// Herotekst volgt de pagina van boenkop.be waar de klant vandaan kwam
// (/studiocamera: gezinstoon), plus de kortingscode uit de kortingsmail.
// Die code is enkel voor een eerste boeking: een ingelogde klant die al
// eerder boekte, krijgt de notitie niet. Met een token maar nog zonder
// klantgegevens (BookingForm haalt die op) wachten we, zodat de notitie
// niet even opflitst bij een terugkerende klant.
const variant = heroVariant()
const mailedCode = getLeadCode()
const leadCode = computed(() => {
  if (authStore.token && !authStore.user) return null
  return authStore.user?.has_confirmed_booking ? null : mailedCode
})

onMounted(async () => {
  if (authStore.isAuthenticated) {
    try {
      await fetchMyTier()
    } catch (error) {
      console.error('Failed to fetch tier info on dashboard:', error)
    }
  }
})
</script>